import React, { useEffect, useRef, useState } from 'react'
import { Box, Flex, Icon, Image, Text } from '@chakra-ui/react'
import { FiCheck } from 'react-icons/fi'

import Logo from '~images/logo/logo.svg'
import { formatClinicName } from '~utils/helpers'
import { SwitchableClinic } from './use-clinic-switcher'

const CHIP_GAP = 8 // px, matches the row gap
const OVERFLOW_RESERVE = 96 // px reserved for the "+N more" chip

type ChipProps = {
    clinic: SwitchableClinic
    isSelected?: boolean
    onClick?: () => void
}

function ClinicChip({ clinic, isSelected, onClick }: ChipProps) {
    return (
        <Flex
            as="button"
            type="button"
            data-chip="true"
            onClick={onClick}
            align="center"
            gap={2}
            px={3}
            h="30px"
            rounded="full"
            flexShrink={0}
            maxW="220px"
            borderWidth={1}
            borderColor={isSelected ? 'brand.500' : 'gray.200'}
            bg={isSelected ? 'brand.50' : 'white'}
            color={isSelected ? 'brand.700' : 'gray.700'}
            cursor={isSelected ? 'default' : 'pointer'}
            transition="background 0.12s, border-color 0.12s"
            _hover={{ bg: isSelected ? 'brand.50' : 'gray.50' }}
        >
            {/* <Image
                src={clinic.avatar_url || undefined}
                fallbackSrc={Logo}
                boxSize="18px"
                objectFit="contain"
                rounded="sm"
                flexShrink={0}
            /> */}
            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                {formatClinicName(clinic.name)}
            </Text>
            {isSelected && (
                <Icon
                    as={FiCheck}
                    color="brand.500"
                    boxSize={3}
                    flexShrink={0}
                />
            )}
        </Flex>
    )
}

type Props = {
    clinics: SwitchableClinic[]
    currentClinicId: string | undefined
    onSelect: (clinic: SwitchableClinic) => void
    onOverflowClick: () => void
}

/**
 * Renders clinics as a horizontal row of pills, showing only as many as fit
 * the available width. The remainder collapse into a "+N more" chip that opens
 * the search dropdown. The currently selected clinic is always rendered first
 * (so it is never hidden) and is highlighted.
 */
export default function ResponsiveClinicChips({
    clinics,
    currentClinicId,
    onSelect,
    onOverflowClick,
}: Props) {
    const wrapRef = useRef<HTMLDivElement>(null)
    const measureRef = useRef<HTMLDivElement>(null)
    const [visibleCount, setVisibleCount] = useState(clinics.length)

    useEffect(() => {
        if (typeof window === 'undefined') return
        const wrap = wrapRef.current
        const measure = measureRef.current
        if (!wrap || !measure) return

        const compute = () => {
            const available = wrap.clientWidth
            const chipEls = Array.from(
                measure.querySelectorAll<HTMLElement>('[data-chip="true"]'),
            )

            let used = 0
            let count = 0
            for (let i = 0; i < chipEls.length; i++) {
                const width = chipEls[i].offsetWidth + (i > 0 ? CHIP_GAP : 0)
                // Reserve room for the overflow chip unless this is the last one.
                const reserve = i < chipEls.length - 1 ? OVERFLOW_RESERVE : 0
                if (used + width + reserve <= available) {
                    used += width
                    count++
                } else {
                    break
                }
            }
            setVisibleCount(Math.max(1, Math.min(count, chipEls.length)))
        }

        compute()
        const observer = new ResizeObserver(compute)
        observer.observe(wrap)
        return () => observer.disconnect()
    }, [clinics])

    const hiddenCount = clinics.length - visibleCount

    return (
        <Box
            ref={wrapRef}
            position="relative"
            flex={1}
            minW={0}
            overflow="hidden"
        >
            {/* Hidden measurement row: always renders every chip so we can
                measure intrinsic widths without affecting layout. */}
            <Flex
                ref={measureRef}
                position="absolute"
                top={0}
                left={0}
                gap={`${CHIP_GAP}px`}
                visibility="hidden"
                pointerEvents="none"
                aria-hidden
            >
                {clinics.map(clinic => (
                    <ClinicChip key={clinic.id} clinic={clinic} />
                ))}
            </Flex>

            {/* Visible row */}
            <Flex gap={`${CHIP_GAP}px`} align="center" flexWrap="nowrap">
                {clinics.slice(0, visibleCount).map(clinic => (
                    <ClinicChip
                        key={clinic.id}
                        clinic={clinic}
                        isSelected={clinic.id === currentClinicId}
                        onClick={() => onSelect(clinic)}
                    />
                ))}
                {hiddenCount > 0 && (
                    <Flex
                        as="button"
                        type="button"
                        onClick={onOverflowClick}
                        align="center"
                        h="30px"
                        px={3}
                        rounded="full"
                        flexShrink={0}
                        borderWidth={1}
                        borderColor="gray.200"
                        bg="white"
                        color="gray.600"
                        fontSize="sm"
                        fontWeight="medium"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                    >
                        +{hiddenCount} more
                    </Flex>
                )}
            </Flex>
        </Box>
    )
}
